import { AxiosResponse, AxiosError } from 'axios';
import moment from 'moment';

import {
    QuantityScheduleDate,
    HarvestEventGroupId,
    HarvestEventUuid,
    HarvestEvent,
    HarvestEvents,
    HarvestEventError,
    HarvestEventsLoadRequestedAction,
    HarvestEventsLoadSucceededAction,
    HarvestEventsLoadFailedAction,
    HarvestEventsLoadCanceledAction,
    HarvestEventsSaveSucceededAction,
    HarvestEventsSaveRequestedAction,
    HarvestEventsSaveFailedAction,
    HarvestEventUpdateAction,
    HarvestEventsUpdateAction,
    HarvestEventsUpdatesPurgeAction,
    HarvestEventValueUpdateActionPayload,
    HarvestEventValueUpdateAction,
    HarvestEventsValueUpdateActionPayload,
    HarvestEventsValueUpdateAction,
    HarvestEventsRemoveAction,
    HarvestEventSetAction,
    HarvestEventsSetAction,
    HarvestEventsMergeAction,
    HarvestEventsDeleteAction,
    HarvestEventErrorsSetAction,
    HarvestEventTimelineUpdateAction,
    HarvestEventTimelineUndoAction
} from './types';
import {
    HARVEST_EVENTS_LOAD_REQUESTED,
    HARVEST_EVENTS_LOAD_SUCCEEDED,
    HARVEST_EVENTS_LOAD_FAILED,
    HARVEST_EVENTS_LOAD_CANCELED,
    HARVEST_EVENTS_SAVE_REQUESTED,
    HARVEST_EVENTS_SAVE_SUCCEEDED,
    HARVEST_EVENTS_SAVE_FAILED,
    HARVEST_EVENT_UPDATE,
    HARVEST_EVENTS_UPDATE,
    HARVEST_EVENT_VALUE_UPDATE,
    HARVEST_EVENTS_VALUE_UPDATE,
    HARVEST_EVENT_REMOVE,
    HARVEST_EVENTS_UPDATES_PURGE,
    HARVEST_EVENT_SET,
    HARVEST_EVENTS_SET,
    HARVEST_EVENTS_MERGE,
    HARVEST_EVENTS_DELETE,
    HARVEST_EVENTS_ERRORS_SET,
    HARVEST_EVENT_TIMELINE_UPDATE,
    HARVEST_EVENT_TIMELINE_UNDO
} from './constants';

// Local Utils
import { formatQuantitySchedules } from './utils';

/**
 * Intended to be called from a component.
 */
export function requestHarvestEventsLoad(): HarvestEventsLoadRequestedAction {
    return {
        type: HARVEST_EVENTS_LOAD_REQUESTED
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param response
 */
export function succeededLoadHarvestEvents(
    response: AxiosResponse
): HarvestEventsLoadSucceededAction {
    return {
        type: HARVEST_EVENTS_LOAD_SUCCEEDED,
        payload: response
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param error
 */
export function failedLoadHarvestEvents(
    error: AxiosError
): HarvestEventsLoadFailedAction {
    return {
        type: HARVEST_EVENTS_LOAD_FAILED,
        payload: error,
        error: true
    };
}

/**
 * Intended to be called from a component.
 */
export function harvestEventsLoadCanceled(): HarvestEventsLoadCanceledAction {
    return {
        type: HARVEST_EVENTS_LOAD_CANCELED
    };
}

/**
 * Intended to be called from either a redux-sagas or component.
 */
export function requestSaveHarvestEvents(): HarvestEventsSaveRequestedAction {
    return {
        type: HARVEST_EVENTS_SAVE_REQUESTED
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param response
 */
export function succeededSaveHarvestEvents(
    response: AxiosResponse
): HarvestEventsSaveSucceededAction {
    return {
        type: HARVEST_EVENTS_SAVE_SUCCEEDED,
        payload: response
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param error
 */
export function failedSaveHarvestEvents(
    error: AxiosError
): HarvestEventsSaveFailedAction {
    return {
        type: HARVEST_EVENTS_SAVE_FAILED,
        payload: error,
        error: true
    };
}

/**
 * Intended to be called from a component.
 * @param entry
 */
export function updateHarvestEvent(
    harvestEvent: HarvestEvent
): HarvestEventUpdateAction {
    const timestamp = moment.utc().format();

    return {
        type: HARVEST_EVENT_UPDATE,
        payload: {
            ...harvestEvent,
            orderedAt: harvestEvent.orderedAt || timestamp,
            quantitySchedules: formatQuantitySchedules(harvestEvent)
        }
    };
}

export function updateHarvestEvents(
    harvestEvents: HarvestEvent[]
): HarvestEventsUpdateAction {
    const timestamp = moment.utc().format();

    return {
        type: HARVEST_EVENTS_UPDATE,
        payload: harvestEvents.reduce(
            (result: HarvestEvents, harvestEvent) => ({
                ...result,
                [harvestEvent.uuid]: {
                    ...harvestEvent,
                    orderedAt: harvestEvent.orderedAt || timestamp,
                    quantitySchedules: formatQuantitySchedules(harvestEvent)
                }
            }),
            {}
        )
    };
}

/**
 * Intended to be called from a component.
 */
export function updateHarvestEventValue({
    harvestEventUuid,
    field,
    value
}: HarvestEventValueUpdateActionPayload): HarvestEventValueUpdateAction {
    return {
        type: HARVEST_EVENT_VALUE_UPDATE,
        payload: {
            harvestEventUuid,
            field,
            value
        }
    };
}

/**
 * Intended to be called from a component.
 */
export function updateHarvestEventsValue({
    harvestEventUuids,
    field,
    value
}: HarvestEventsValueUpdateActionPayload): HarvestEventsValueUpdateAction {
    return {
        type: HARVEST_EVENTS_VALUE_UPDATE,
        payload: {
            harvestEventUuids,
            field,
            value
        }
    };
}

/**
 * Intended to be called from a component.
 */
export function removeHarvestEvent(
    harvestEventUuid: HarvestEventUuid
): HarvestEventsRemoveAction {
    return {
        type: HARVEST_EVENT_REMOVE,
        payload: harvestEventUuid
    };
}

/**
 * Intended to be called from a component.
 */
export function purgeHarvestEventsUpdates(): HarvestEventsUpdatesPurgeAction {
    return {
        type: HARVEST_EVENTS_UPDATES_PURGE
    };
}

/**
 * Intended to be called from a component.
 */
export function updateHarvestEventTimeline(
    harvestEventUuid: HarvestEventUuid,
    previousGroupId?: HarvestEventGroupId,
    nextGroupId?: HarvestEventGroupId,
    date?: QuantityScheduleDate
): HarvestEventTimelineUpdateAction {
    return {
        type: HARVEST_EVENT_TIMELINE_UPDATE,
        payload: {
            harvestEventUuid,
            previousGroupId,
            nextGroupId,
            date
        }
    };
}

/**
 * Intended to be called from a component.
 */
export function undoHarvestEventTimeline(): HarvestEventTimelineUndoAction {
    return {
        type: HARVEST_EVENT_TIMELINE_UNDO
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setHarvestEvent(
    harvestEvent: HarvestEvent
): HarvestEventSetAction {
    return {
        type: HARVEST_EVENT_SET,
        payload: harvestEvent
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setHarvestEvents(
    harvestEvents: HarvestEvents
): HarvestEventsSetAction {
    return {
        type: HARVEST_EVENTS_SET,
        payload: harvestEvents
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param harvestEvents
 */
export function mergeHarvestEvents(
    harvestEvents: HarvestEvents
): HarvestEventsMergeAction {
    return {
        type: HARVEST_EVENTS_MERGE,
        payload: harvestEvents
    };
}

/**
 * Only intended for use in redux-sagas.
 * @param harvestEventUuid
 */
export function deleteHarvestEvents(
    harvestEventUuids: HarvestEventUuid | HarvestEventUuid[]
): HarvestEventsDeleteAction {
    return {
        type: HARVEST_EVENTS_DELETE,
        payload: harvestEventUuids
    };
}

/**
 * Only intended for use in redux-sagas.
 */
export function setHarvestEventErrors(
    harvestEventErrors: HarvestEventError[]
): HarvestEventErrorsSetAction {
    return {
        type: HARVEST_EVENTS_ERRORS_SET,
        payload: harvestEventErrors
    };
}
